// Spec 078 FR-032 — a versão acessível da cena: uma tabela por máquina com status (dot + texto),
// ritmo e parada térmica. Alcançável a qualquer momento pelo HUD; é também o fallback sem WebGL
// da rota ambiente (`size="far"`). Sem three.js aqui de propósito.
//
// "Vazão" e "Nominal" eram duas colunas de números crus e deixavam a divisão por conta de quem lê.
// Viraram UMA coluna "Ritmo": barra + porcentagem + "X de Y kg/h (estimado)". A comparação é a
// informação; os números soltos eram a matéria-prima dela.
import * as React from "react";
import { Dot } from "@/components/ui/dot";
import { RateMeter } from "./RateMeter";
import {
  SCENE_STATUS_LABELS,
  type LineSceneResponse,
  type SceneMachine,
} from "@/types/scene.types";

// Spec 080 — a coluna deixou de mostrar medição: o que importa é DESDE QUANDO a máquina parou.
const clock = new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" });

export interface SceneTextPanelProps {
  scene: LineSceneResponse;
  size?: "normal" | "far";
  className?: string;
}

/**
 * Spec 080 — "—" ⇒ nenhuma parada térmica em aberto (a máquina pode estar parada por outro motivo,
 * e essa distinção é o que a feature existe para dar). O estado de reconhecimento vem do EVENTO, e
 * por isso continua correto quando não há alarme algum (parada fora de produção).
 */
export function heatText(m: SceneMachine): string {
  const h = m.heat;
  if (!h) return "—";
  const ack = h.acknowledged ? "reconhecida" : "nova";
  return `Parada térmica (${ack}) · desde ${clock.format(new Date(h.trippedAt))}`;
}

export function SceneTextPanel({
  scene,
  size = "normal",
  className = "",
}: SceneTextPanelProps): React.ReactElement {
  const far = size === "far";
  const cell = far ? "px-4 py-3 text-2xl" : "px-3 py-1.5 text-sm";
  return (
    <div className={`overflow-x-auto ${className}`} data-testid="scene-text-panel">
      <table className="w-full border-collapse text-left">
        <caption className={`text-text-secondary ${far ? "py-2 text-xl" : "py-1 text-xs"}`}>
          Estado por máquina — {scene.meta.lineName}
          {scene.meta.layoutSource === "fallback" ? " · disposição provisória" : ""}
        </caption>
        <thead>
          <tr className="border-b border-border-subtle text-text-tertiary">
            <th scope="col" className={cell}>
              Máquina
            </th>
            <th scope="col" className={cell}>
              Status
            </th>
            <th scope="col" className={cell}>
              Ritmo
            </th>
            <th scope="col" className={cell}>
              Parada térmica
            </th>
          </tr>
        </thead>
        <tbody>
          {scene.machines.map((m) => {
            const st = SCENE_STATUS_LABELS[m.status];
            return (
              <tr key={m.id} className="border-border-subtle/60 border-b" data-machine-id={m.id}>
                <th scope="row" className={`${cell} font-medium text-text-primary`}>
                  {m.name}
                </th>
                <td className={cell}>
                  <span className="inline-flex items-center gap-1.5">
                    <Dot tone={st.tone} size={far ? 14 : 8} />
                    {st.label}
                  </span>
                </td>
                <td className={cell}>
                  <RateMeter machine={m} variant="panel" far={far} />
                </td>
                <td className={cell}>{heatText(m)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
